import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { normalizeAuthEmail, readFunctionsHttpErrorBody } from "@mixitone/util";
import { SessionAdapter, isAuthEvent } from "./SessionAdapter";

function adaptedSession(session: Session) {
  return { ...session, adapter: "supabase" };
}

export class SupabaseSessionAdapter extends SessionAdapter {
  async initialize(): Promise<void> {
    // Nothing to do
  }

  constructor(private supabase: SupabaseClient) {
    super();

    // I needed to add this after updating supbase gotrue-js to 2.46.1
    // due to https://github.com/supabase/gotrue-js/pull/747
    this.supabase.auth.initialize();

    this.supabase.auth.onAuthStateChange(async (event, supabaseSession) => {
      if (isAuthEvent(event)) {
        const session = supabaseSession ? adaptedSession(supabaseSession) : null;
        this.emit(event, session);
      }
    });
  }

  get canSignUp() {
    return true;
  }

  get canSignIn() {
    return true;
  }

  get canSignOut() {
    return true;
  }

  get canResetPassword() {
    return true;
  }

  async signOut() {
    const { error } = await this.supabase.auth.signOut();
    if (error) return { error };
    return { data: undefined };
  }

  async signUp(
    email: string,
    password: string,
    options?: {
      data: {
        name: string;
        country?: string;
        timezone?: string;
        inviteToken?: string;
        registerClubData?: Record<string, any>;
      };
    },
  ) {
    return await this.supabase.auth.signUp({ email: normalizeAuthEmail(email), password, options });
  }

  async signInWithPassword(email: string, password: string) {
    const { error, data } = await this.supabase.auth.signInWithPassword({
      email: normalizeAuthEmail(email),
      password,
    });
    if (!data) {
      return { error: new Error("No session returned") };
    }
    const { user, session } = data;
    if (!(session && user)) {
      return { error: error || new Error("No session or user returned") };
    }

    return {
      data: {
        session: adaptedSession(session),
        user,
      },
    };
  }

  async resetPasswordForEmail(email: string, redirectTo: string) {
    const { error } = await this.supabase.auth.resetPasswordForEmail(normalizeAuthEmail(email), {
      redirectTo,
    });
    if (error) return { error };
    return { data: {} };
  }

  async verifyRecoveryOtp(email: string, token: string) {
    const { error, data } = await this.supabase.auth.verifyOtp({
      email: normalizeAuthEmail(email),
      token: token.replace(/\D/g, ""),
      type: "recovery",
    });
    if (error) return { error };
    if (!data?.session || !data.user) {
      return { error: error || new Error("No session or user returned") };
    }
    return {
      data: {
        session: adaptedSession(data.session),
        user: data.user,
      },
    };
  }

  async updatePassword(password: string) {
    const { error } = await this.supabase.auth.updateUser({ password });
    if (error) return { error };
    return { data: {} };
  }

  async updateName(name: string) {
    const { error } = await this.supabase.auth.updateUser({ data: { name } });
    if (error) return { error };
    return { data: {} };
  }

  async updateEmail(email: string) {
    const { error } = await this.supabase.auth.updateUser({ email: normalizeAuthEmail(email) });
    if (error) return { error };
    return { data: {} };
  }

  async getUser() {
    const { error, data } = await this.supabase.auth.getUser();
    if (!data) return { error: error || new Error("No user returned") };
    if (!data.user) return { error: error || new Error("No user returned") };
    return { data };
  }

  async getSession() {
    const { data } = await this.supabase.auth.getSession();
    if (!data?.session) return null;
    return adaptedSession(data.session);
  }

  async listTotpFactors() {
    const { data, error } = await this.supabase.auth.mfa.listFactors();
    if (error) return { error };
    const factors = (data?.all ?? data?.totp ?? [])
      .filter((factor) => factor.factor_type === "totp")
      .map((factor) => ({
        id: factor.id,
        friendly_name: factor.friendly_name,
        status: factor.status as "verified" | "unverified",
      }));
    return { data: { factors } };
  }

  async hasTotpFactor() {
    const result = await this.listTotpFactors();
    if (result.error) return { error: result.error };
    const enrolled = (result.data?.factors ?? []).some((factor) => factor.status === "verified");
    return { data: { enrolled } };
  }

  async enrollTotp(friendlyName = "Authenticator") {
    const { data, error } = await this.supabase.auth.mfa.enroll({
      factorType: "totp",
      friendlyName,
      issuer: "MixItOne",
    });
    if (error) return { error };
    if (!data?.id || !data.totp?.qr_code || !data.totp?.secret || !data.totp?.uri) {
      return { error: new Error("MFA enrollment did not return a factor") };
    }
    return {
      data: {
        factorId: data.id,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
        uri: data.totp.uri,
      },
    };
  }

  async verifyTotpEnrollment(factorId: string, code: string) {
    const { data, error } = await this.supabase.auth.mfa.challengeAndVerify({
      factorId,
      code,
    });
    if (error) return { error };
    return { data: { accessToken: data?.access_token } };
  }

  async challengeAndVerifyTotp(factorId: string, code: string) {
    const { data, error } = await this.supabase.auth.mfa.challengeAndVerify({
      factorId,
      code,
    });
    if (error) return { error };
    return { data: { accessToken: data?.access_token } };
  }

  async applyElevatedAccessToken(accessToken: string) {
    const current = await this.supabase.auth.getSession();
    const refreshToken = current.data.session?.refresh_token;
    if (!refreshToken) {
      return { error: new Error("No session to elevate") };
    }
    const { error } = await this.supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (error) return { error };
    return { data: undefined };
  }

  async unenrollTotp(factorId: string) {
    const { error } = await this.supabase.auth.mfa.unenroll({ factorId });
    if (error) return { error };
    return { data: undefined };
  }

  rpc<T>(
    functionName: string,
    args: any,
  ): Promise<
    | {
        error: undefined;
        data: T;
      }
    | { error: Error; data: undefined }
  > {
    return new Promise((resolve, reject) => {
      this.supabase.rpc(functionName, args).then(
        (response) => {
          resolve(response as any);
        },
        (reason) => reject(reason),
      );
    });
  }

  async edgeFunction(functionName: string, args: any) {
    const response = await this.supabase.functions.invoke(functionName, {
      body: JSON.stringify(args),
    });
    if (!response.error) return response;

    // Non-2xx responses usually have data=null; the JSON body is on error.context (Response).
    const data = response.data ?? (await readFunctionsHttpErrorBody(response.error));
    return { data, error: response.error };
  }
}
